import { useCallback, useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import styled from '@emotion/styled';
import { useMentionAtom } from '@remirror/react';
import type { AtMentionItem } from './AtMentionContext';
import { useAtMentionContext } from './AtMentionContext';
import { useAtMentionSuggestions } from './useAtMentionSuggestions';

type MentionItem = AtMentionItem & {
  id: string;
  label: string;
};

type AtMentionDropdownProps = {
  editorFocused: boolean;
};

export const AtMentionDropdown = ({ editorFocused }: AtMentionDropdownProps) => {
  const [, , displayConfig] = useAtMentionContext();
  const [mentionItems, setMentionItems] = useState<MentionItem[]>([]);

  const { state, getMenuProps, getItemProps } = useMentionAtom<MentionItem>({
    items: mentionItems,
  });

  // Keep suggestions open only while mention query is active and editor is focused.
  const enabled = !!state && editorFocused;

  const query = state?.query?.full?.toLowerCase() ?? '';
  const { items: fetchedItems, isLoading, hasMore, loadMore } = useAtMentionSuggestions(query, enabled);

  useEffect(() => {
    const adapted = fetchedItems.map((item) => ({
      ...item,
      id: item.entityId,
      label: item.name,
    }));
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mentionAtom requires local items state fed by async suggestions.
    setMentionItems(adapted);
  }, [fetchedItems]);

  const getSubtitle = useCallback((item: MentionItem): string | undefined => {
    return item.subtitle ?? item.entityId;
  }, []);

  const renderItem = useCallback(
    (item: MentionItem, index: number) => {
      const itemProps = getItemProps({ item, index });
      const subtitle = getSubtitle(item);

      return (
        <MenuItem key={item.entityId} {...itemProps}>
          <ItemName>{item.name}</ItemName>
          {subtitle !== undefined && <ItemSubtitle>{subtitle}</ItemSubtitle>}
        </MenuItem>
      );
    },
    [getItemProps, getSubtitle]
  );

  if (!enabled) {
    return null;
  }

  const menuProps = getMenuProps();

  return (
    <MenuContainer>
      <AtMentionMenuContent
        enabled={enabled}
        headerLabel={displayConfig?.headerLabel}
        menuProps={menuProps}
        mentionItems={mentionItems}
        isLoading={isLoading}
        hasMore={hasMore}
        renderItem={renderItem}
        loadMore={loadMore}
      />
    </MenuContainer>
  );
};

AtMentionDropdown.displayName = 'AtMentionDropdown';

function AtMentionMenuContent({
  enabled,
  headerLabel,
  menuProps,
  mentionItems,
  isLoading,
  hasMore,
  renderItem,
  loadMore,
}: {
  enabled: boolean;
  headerLabel?: string;
  menuProps: object;
  mentionItems: MentionItem[];
  isLoading: boolean;
  hasMore: boolean;
  renderItem: (item: MentionItem, index: number) => ReactNode;
  loadMore: () => void;
}) {
  const handleLoadMoreMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
    // Prevent editor from losing focus when clicking Load More
    e.preventDefault();
    loadMore();
  };

  return (
    <MenuPanel>
      {enabled && headerLabel && <MenuHeader>{headerLabel}</MenuHeader>}
      <Menu {...menuProps}>
        {enabled && mentionItems.map((item, index) => renderItem(item, index))}
        {enabled && !mentionItems.length && !isLoading && <NoResults>No results</NoResults>}
        {enabled && isLoading && mentionItems.length === 0 && <NoResults>Loading...</NoResults>}
        {enabled && hasMore && !isLoading && (
          <LoadMore onMouseDown={handleLoadMoreMouseDown}>Load more</LoadMore>
        )}
      </Menu>
    </MenuPanel>
  );
}

const MenuContainer = styled('div')({
  position: 'absolute',
  bottom: 'calc(100% + 8px)',
  left: 0,
  right: 0,
  zIndex: 1300,
  display: 'flex',
  justifyContent: 'flex-start',
});

const MenuPanel = styled('div')(({ theme }) => ({
  width: 'min(100%, 1400px)',
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.gray.light2}`,
  borderRadius: 8,
  boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
  overflow: 'hidden',
}));

const MenuHeader = styled('div')(({ theme }) => ({
  padding: '12px',
  fontFamily: theme.fonts.semiBold,
  fontSize: 11,
  color: theme.palette.black.light1,
  backgroundColor: theme.palette.gray.light3,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
}));

const Menu = styled('div')({
  maxHeight: 360,
  overflowY: 'auto' as const,
  minWidth: 320,
});

const MenuItem = styled('div')(({ theme }) => {
  const focusTextColor = theme.palette.wl.enabled ? theme.palette.wl.primary.main : theme.palette.hyperlink;
  const focusBackgroundColor = theme.palette.wl.enabled
    ? theme.palette.wl.prompt.light6
    : theme.palette.blue.light3;

  return {
    padding: '8px 12px',
    cursor: 'pointer',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.white.main,
    '&:hover': {
      color: focusTextColor,
      backgroundColor: focusBackgroundColor,
    },
  };
});

const ItemName = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  color: 'inherit',
}));

const ItemSubtitle = styled('div')(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 12,
  color: theme.palette.gray.main,
  marginTop: 2,
}));

const NoResults = styled('div')(({ theme }) => ({
  padding: '8px 12px',
  fontFamily: theme.fonts.regular,
  fontSize: 14,
  color: theme.palette.gray.main,
}));

const LoadMore = styled('button')(({ theme }) => ({
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontFamily: theme.fonts.regular,
  fontSize: 14,
  color: theme.palette.primary.main,
  textAlign: 'left' as const,
  '&:hover': {
    textDecoration: 'underline',
  },
}));
